import i18n from "i18next";
import k from "src/i18n/keys";

import type { FC, RefObject, MouseEvent } from "react";
import { useSelector, useDispatch } from "react-redux";
import ReactFC from "react-fusioncharts";
import * as R from "ramda";

import Loader from "src/components/Dashboard/Body/Loader";
import { setReports } from "src/actions/reports";
import { setFieldExpansion } from "src/actions/checklist";
import {
  updateInstanceFilter,
  setAllRecordsFilter,
  filterEmbeddedFields,
} from "src/actions/workflows";
import {
  getChartDataLoading,
  getChartReportId,
  getChecklistFieldsById,
  getAllWorkflows,
  getAllRecords,
  getCurrentUser,
  getWorkflowInstancesById,
  getChartTitleForExport,
} from "src/reducers";
import { setNewChartDialogAttributes, expandChart } from "src/actions/chart";
import { getModifiedChartData } from "src/utils/charts.js";

import worker from "src/workers/app.worker";

import type { AppState, InstancesById } from "src/types";

interface Props {
  chartId: number | string;
  chartData: { [key: string]: unknown };
  height?: string;
  width?: string;
  chartRef: RefObject<unknown>;
  title: string;
  titleFontSize?: number;
}

interface Filter {
  [key: string]: unknown;
}

interface WorkerData {
  workflow: unknown;
  fieldsById: { [key: string]: unknown };
  instancesById: { [key: string]: unknown };
  instanceFilter: unknown;
  chatroomAttributes: string[];
  updatedInstances: { [key: string]: unknown };
  allRecords: { [key: string]: unknown };
  currentUserUid?: string;
}

declare global {
  interface Window {
    openchart: (filters: string) => void;
  }
}

interface FilterEmbeddedFieldsPayload {
  instances: InstancesById;
}

const Visualization: FC<Props> = ({
  chartId,
  chartData,
  height = "100%",
  width = "100%",
  chartRef,
  title,
  titleFontSize = 16,
}) => {
  const dispatch = useDispatch();
  const workflow = useSelector(({ app }: { app: AppState }) =>
    getAllWorkflows(app),
  );
  const instanceFilter = workflow.instanceFilter;
  const allRecords = useSelector(({ app }: { app: AppState }) =>
    getAllRecords(app),
  );
  const fieldsById = useSelector(({ app }: { app: AppState }) =>
    getChecklistFieldsById(app),
  );
  const currentUser = useSelector(({ app }: { app: AppState }) =>
    getCurrentUser(app),
  );
  const instancesById = useSelector(({ app }: { app: AppState }) =>
    getWorkflowInstancesById(app),
  );
  const exportChartTitle = useSelector(
    ({ app, chartId }: { app: AppState }) =>
      chartId && getChartTitleForExport(app, parseInt(chartId.toString())),
  );
  const chatroomAttributes = ["owner", "dueDate", "status"];
  const updatedInstances = { ...instancesById };

  const isEmbeddedField = (field: string): boolean => {
    const splittedField = field.split(">");
    return splittedField.length > 1;
  };

  const workerData: WorkerData = {
    workflow,
    fieldsById: fieldsById.toJS(),
    instancesById,
    instanceFilter,
    chatroomAttributes,
    updatedInstances,
    allRecords,
    currentUserUid: currentUser?.uid,
  };

  const reportId = useSelector(({ app }: { app: AppState }) =>
    getChartReportId(app, chartId),
  );

  const loading = useSelector(({ app }: { app: AppState }) =>
    getChartDataLoading(app, chartId),
  );

  if (loading) {
    return <Loader />;
  }

  const handleDeepEmbeddedFilter = ({
    key,
    filter,
  }: {
    key: string;
    filter: Filter;
  }) => {
    const [parentId = "", ...rest] = key.split(">");
    // Filter applied only for embedded field keys
    if (rest.length > 0) {
      dispatch(setFieldExpansion({ id: parentId, value: true }));
      dispatch(setAllRecordsFilter({ columnId: parentId, value: true }));

      // Double-cast needed to satisfy Worker constructor in TypeScript
      const webWorker = new Worker(worker as unknown as string);
      webWorker.addEventListener("message", (event: MessageEvent) => {
        const payload: FilterEmbeddedFieldsPayload = { instances: event.data };
        dispatch(filterEmbeddedFields(payload.instances));
      });

      webWorker.postMessage({
        ...workerData,
        payload: filter,
        allRecords: {
          ...workerData.allRecords,
          [parentId]: true,
        },
      });
    }
  };

  const dataPlotClick = (e: MouseEvent) => {
    window.openchart = (filters: string) => {
      e.stopPropagation();
      e.preventDefault();

      dispatch(setNewChartDialogAttributes({ show: false }));
      dispatch(expandChart(chartId));

      // If the user is not on the manage view, then redirect to the
      // manage view.
      if (!location.pathname.includes("/reports")) {
        dispatch(setReports(reportId));
      }

      // If the field is an embedded field, apply deep filter
      Object.entries(JSON.parse(filters)).reduce((acc, [key, value]) => {
        if (isEmbeddedField(filters)) {
          const parentId = key.split(">")[0];
          // Constructing instance filter object for the selected field
          const filterInstance: Filter = {
            [key.replace(/>/g, "-")]: [value],
            id: instanceFilter.id,
            chartId,
            page: 1,
            reportId: reportId,
            deepFilter: [parentId],
          } as Filter;
          handleDeepEmbeddedFilter({ key, filter: filterInstance });
        }
        return acc;
      }, {});

      const parsedFilters = Object.entries(JSON.parse(filters)).reduce(
        (acc, [key, value]) => {
          // Replace > with - on filters to match our chart form
          // filter implementation
          return { ...acc, [key.replace(/>/g, "-")]: value };
        },
        {},
      );

      dispatch(
        updateInstanceFilter(
          // Make null or empty values "null" to make it work with blank filter.
          R.map(
            (value) => (value === null || R.isEmpty(value) ? "null" : value),
            parsedFilters,
          ),
        ),
      );
    };
  };

  const modifiedChartData = getModifiedChartData({
    // @ts-expect-error - Do not have clarity on the schema or chartData
    type: chartData.type,
    chartData,
    chartId,
    onDataPlotClick: dataPlotClick,
    exportChartTitle,
    title,
    titleFontSize,
  });

  const chartConfigs = {
    // @ts-expect-error - Do not have clarity on the schema or chartData
    type: modifiedChartData.type,
    height: height,
    width: width,
    dataFormat: "json",
    // @ts-expect-error - Do not have clarity on the schema or chartData
    dataSource: modifiedChartData.dataSource,
    events: { dataPlotClick },
    dataLoadErrorMessage: i18n.t(k.FAILED_TO_LOAD_CHART),
    typeNotSupportedMessage: i18n.t(k.NO_DATA_TO_DISPLAY),
    dataEmptyMessage: i18n.t(k.NO_DATA_TO_DISPLAY),
  };

  const ChartComponent = ReactFC as any;

  return <ChartComponent {...chartConfigs} ref={chartRef} />;
};

export default Visualization;
